import { KoobButton, KoobPill, KoobPillGroup } from '@koob/ui';
import { useLibraryFilters, useSetLibraryFilters } from '@koob/trip-designer';
import { useTranslation } from 'react-i18next';

const LibraryActiveFilters = ({ style }) => {
  const { t } = useTranslation(['tripDesigner', 'misc']);
  const libraryFilters = useLibraryFilters();
  const setLibraryFilters = useSetLibraryFilters();
  const {
    countries,
    cities,
    tripDuration,
    dmcId,
    users,
    themes,
    segments,
    tags,
    departureLocation,
    returnLocation,
    guided,
  } = libraryFilters ?? {};

  const clearFilter = (property, value) =>
    setLibraryFilters(current => ({ ...current, [property]: value }));

  const hasActiveFilters =
    (countries || []).length > 0 ||
    (cities || []).length > 0 ||
    Boolean(tripDuration) ||
    Boolean(dmcId) ||
    Boolean(users) ||
    (themes || []).length > 0 ||
    (segments || []).length > 0 ||
    (tags || []).length > 0 ||
    Boolean(departureLocation?.[0]) ||
    Boolean(returnLocation?.[0]) ||
    guided === true ||
    guided === false;

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div style={style}>
      <KoobPillGroup>
        {(countries || []).map(({ value, label }) => (
          <KoobPill
            key={value}
            onRemove={() =>
              clearFilter(
                'countries',
                (countries || []).filter(option => option.value !== value),
              )
            }
          >
            {label}
          </KoobPill>
        ))}

        {(cities || []).map((city, index) => (
          <KoobPill
            key={city?.id ?? index}
            onRemove={() =>
              clearFilter(
                'cities',
                (cities || []).filter((_, i) => i !== index),
              )
            }
          >
            {city?.title}
          </KoobPill>
        ))}

        {Boolean(tripDuration) && (
          <KoobPill onRemove={() => clearFilter('tripDuration', null)}>
            {t('search.expDurationCount', { count: tripDuration })}
          </KoobPill>
        )}

        {Boolean(dmcId) && (
          <KoobPill onRemove={() => clearFilter('dmcId', null)}>
            {dmcId?.label}
          </KoobPill>
        )}

        {Boolean(users) && (
          <KoobPill onRemove={() => clearFilter('users', null)}>
            {users?.firstName} {users?.lastName}
          </KoobPill>
        )}

        {(themes || []).map(({ id, displayName }) => (
          <KoobPill
            key={id}
            onRemove={() =>
              clearFilter(
                'themes',
                (themes || []).filter(option => option.id !== id),
              )
            }
          >
            {displayName}
          </KoobPill>
        ))}

        {(segments || []).map(({ id, displayName }) => (
          <KoobPill
            key={id}
            onRemove={() =>
              clearFilter(
                'segments',
                (segments || []).filter(option => option.id !== id),
              )
            }
          >
            {displayName}
          </KoobPill>
        ))}

        {(tags || []).map(({ id, name }) => (
          <KoobPill
            key={id}
            onRemove={() =>
              clearFilter(
                'tags',
                (tags || []).filter(option => option.id !== id),
              )
            }
          >
            {name}
          </KoobPill>
        ))}

        {Boolean(departureLocation?.[0]) && (
          <KoobPill onRemove={() => clearFilter('departureLocation', [])}>
            {t('designTripModal.form.departureLocation')}:{' '}
            {departureLocation[0]?.title}
          </KoobPill>
        )}

        {Boolean(returnLocation?.[0]) && (
          <KoobPill onRemove={() => clearFilter('returnLocation', [])}>
            {t('designTripModal.form.returnLocation')}:{' '}
            {returnLocation[0]?.title}
          </KoobPill>
        )}

        {(guided === true || guided === false) && (
          <KoobPill onRemove={() => clearFilter('guided', undefined)}>
            {guided ? t('search.guided') : t('search.notGuided')}
          </KoobPill>
        )}

        <KoobButton
          variant="ghost"
          size="sm"
          onClick={() =>
            setLibraryFilters(current => ({
              ...current,
              countries: [],
              cities: [],
              tripDuration: null,
              dmcId: null,
              users: null,
              themes: [],
              segments: [],
              tags: [],
              departureLocation: [],
              returnLocation: [],
              guided: undefined,
            }))
          }
        >
          {t('misc:filters.clearAll')}
        </KoobButton>
      </KoobPillGroup>
    </div>
  );
};

export default LibraryActiveFilters;
